import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { useToast } from '../hooks/use-toast';
import { CalendarDays, Plus, Trash2, Pencil, AlertTriangle, Repeat, X, ChevronLeft, ChevronRight, Copy, Plane } from 'lucide-react';

const API = `${window.__BACKEND_URL__}/api`;
const WEEKDAYS = [
  { i: 0, l: 'Mon' }, { i: 1, l: 'Tue' }, { i: 2, l: 'Wed' }, { i: 3, l: 'Thu' },
  { i: 4, l: 'Fri' }, { i: 5, l: 'Sat' }, { i: 6, l: 'Sun' },
];

const isoLocal = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
const isoToday = () => isoLocal(new Date());
const addDays = (isoStr, n) => { const d = new Date(`${isoStr}T00:00:00`); d.setDate(d.getDate() + n); return isoLocal(d); };
const mondayOf = (isoStr) => { const d = new Date(`${isoStr}T00:00:00`); const back = (d.getDay() + 6) % 7; d.setDate(d.getDate() - back); return isoLocal(d); };
const dayDiff = (a, b) => Math.round((new Date(`${b}T00:00:00`) - new Date(`${a}T00:00:00`)) / 86400000);

const emptyForm = () => ({
  title: '', start_date: isoToday(), end_date: isoToday(),
  start_time: '08:00', end_time: '16:00', recurrence: 'once',
  days_of_week: [], member_ids: [], job_id: '', notes: '',
});

const inputCls = 'w-full mt-1 px-3 py-2 border border-gray-300 dark:border-[#2D3748] rounded-lg bg-white dark:bg-[#1A2433] text-gray-900 dark:text-gray-100 text-sm';

export default function TeamScheduleManager({ teamId, teamName, members, jobs, token }) {
  const { toast } = useToast();
  const [shifts, setShifts] = useState([]);
  const [occurrences, setOccurrences] = useState([]);
  const [timeOff, setTimeOff] = useState([]);
  const [weekStart, setWeekStart] = useState(() => mondayOf(isoToday()));
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [conflicts, setConflicts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [copying, setCopying] = useState(false);
  const [dragOverDay, setDragOverDay] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${API}/teams/${teamId}/shifts?start=${weekStart}&end=${addDays(weekStart, 6)}`, { headers }
      );
      setShifts(data.shifts || []);
      setOccurrences(data.occurrences || []);
      setTimeOff(data.time_off || []);
    } catch (e) { /* supervisors without access simply see nothing */ }
    // eslint-disable-next-line
  }, [teamId, token, weekStart]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setShowForm(false); setConflicts([]); }, [teamId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleIn = (k, v) => setForm((f) => {
    const cur = new Set(f[k]);
    cur.has(v) ? cur.delete(v) : cur.add(v);
    return { ...f, [k]: Array.from(cur) };
  });

  const openCreate = (date) => {
    setForm({ ...emptyForm(), start_date: date || isoToday(), end_date: date || isoToday() });
    setEditingId(null); setConflicts([]); setShowForm(true);
  };
  const openEdit = (s) => {
    setForm({
      title: s.title || '', start_date: s.start_date, end_date: s.end_date || s.start_date,
      start_time: s.start_time, end_time: s.end_time, recurrence: s.recurrence || 'once',
      days_of_week: s.days_of_week || [], member_ids: s.member_ids || [], job_id: s.job_id || '', notes: s.notes || '',
    });
    setEditingId(s.id); setConflicts([]); setShowForm(true);
  };

  const save = async () => {
    if (form.recurrence === 'weekly' && form.days_of_week.length === 0) {
      toast({ title: 'Pick at least one weekday for a weekly shift', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, team_id: teamId, job_id: form.job_id || null, end_date: form.end_date || form.start_date };
      const { data } = editingId
        ? await axios.put(`${API}/shifts/${editingId}`, payload, { headers })
        : await axios.post(`${API}/shifts`, payload, { headers });
      setConflicts(data.conflicts || []);
      toast({
        title: editingId ? 'Shift updated' : 'Shift scheduled',
        description: (data.conflicts || []).length
          ? `Saved, but ${data.conflicts.length} scheduling conflict(s) detected — see warning below.`
          : 'Scheduled workers have been notified.',
      });
      setShowForm(false);
      load();
    } catch (e) {
      toast({ title: 'Could not save shift', description: e.response?.data?.detail || 'Unknown error', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.title || 'Shift'}"? Scheduled workers will be notified.`)) return;
    try {
      await axios.delete(`${API}/shifts/${s.id}`, { headers });
      toast({ title: 'Shift deleted' });
      load();
    } catch (e) {
      toast({ title: 'Delete failed', description: e.response?.data?.detail || 'Unknown error', variant: 'destructive' });
    }
  };

  const copyLastWeek = async () => {
    setCopying(true);
    try {
      const { data } = await axios.post(`${API}/shifts/copy-week`, {
        team_id: teamId, source_week_start: addDays(weekStart, -7), target_week_start: weekStart,
      }, { headers });
      toast({
        title: data.created > 0 ? `Copied ${data.created} shift(s) from last week` : 'Nothing to copy',
        description: data.created > 0 ? 'Scheduled workers have been notified.' : 'Last week has no one-time shifts (weekly shifts already repeat).',
      });
      load();
    } catch (e) {
      toast({ title: 'Copy failed', description: e.response?.data?.detail || 'Unknown error', variant: 'destructive' });
    } finally { setCopying(false); }
  };

  const onDrop = async (targetDate, e) => {
    e.preventDefault();
    setDragOverDay(null);
    let payload;
    try { payload = JSON.parse(e.dataTransfer.getData('text/plain')); } catch { return; }
    const shift = shifts.find((s) => s.id === payload.shift_id);
    if (!shift || payload.date === targetDate) return;
    if (shift.recurrence === 'weekly') {
      toast({ title: "Weekly shifts can't be moved by drag", description: 'Edit the shift to change its weekdays instead.' });
      return;
    }
    const delta = dayDiff(payload.date, targetDate);
    try {
      const { data } = await axios.put(`${API}/shifts/${shift.id}`, {
        start_date: addDays(shift.start_date, delta),
        end_date: addDays(shift.end_date || shift.start_date, delta),
      }, { headers });
      setConflicts(data.conflicts || []);
      toast({
        title: `Moved to ${targetDate}`,
        description: (data.conflicts || []).length ? `${data.conflicts.length} conflict(s) detected — see warning.` : 'Scheduled workers have been notified.',
      });
      load();
    } catch (err) {
      toast({ title: 'Move failed', description: err.response?.data?.detail || 'Unknown error', variant: 'destructive' });
    }
  };

  const weekDates = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const occByDate = occurrences.reduce((acc, o) => { (acc[o.date] = acc[o.date] || []).push(o); return acc; }, {});
  const ptoByDate = timeOff.reduce((acc, p) => { (acc[p.date] = acc[p.date] || []).push(p); return acc; }, {});
  const today = isoToday();
  const weekLabel = `${new Date(`${weekStart}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${new Date(`${addDays(weekStart, 6)}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;

  return (
    <Card className="shadow-xl" data-testid="team-schedule-card">
      <CardHeader className="bg-blue-50 dark:bg-[#1A2433]">
        <CardTitle className="text-xl flex items-center justify-between text-gray-900 dark:text-gray-100">
          <span className="flex items-center">
            <CalendarDays className="w-6 h-6 mr-2 text-[#1E88E5]" />
            Shift Schedule — {teamName}
          </span>
          <Button size="sm" onClick={showForm ? () => setShowForm(false) : () => openCreate()} data-testid="add-shift-btn"
            className="bg-[#1E88E5] hover:bg-blue-700 text-white">
            {showForm ? <X className="w-4 h-4 mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
            {showForm ? 'Close' : 'Add Shift'}
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6 space-y-5">
        {conflicts.length > 0 && (
          <div className="p-3 rounded-lg border border-amber-400 bg-amber-50 dark:bg-amber-950/30" data-testid="shift-conflicts-banner">
            <p className="text-sm font-semibold text-amber-700 dark:text-amber-300 flex items-center gap-1">
              <AlertTriangle className="w-4 h-4" /> Scheduling conflicts
            </p>
            <ul className="text-xs text-amber-700 dark:text-amber-300 mt-1 list-disc pl-5">
              {conflicts.map((c, i) => (
                <li key={i}>{c.date}: overlaps "{c.other_shift}" for {c.members.join(', ')}</li>
              ))}
            </ul>
          </div>
        )}

        {showForm && (
          <div className="p-4 rounded-lg border-2 border-blue-200 dark:border-[#2D3748] bg-blue-50/50 dark:bg-[#0F1923] space-y-4" data-testid="shift-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-semibold">Shift title</Label>
                <Input value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="e.g. Day Shift" className="mt-1" data-testid="shift-title-input" />
              </div>
              <div>
                <Label className="text-sm font-semibold">Job (optional)</Label>
                <select value={form.job_id} onChange={(e) => set('job_id', e.target.value)} className={inputCls} data-testid="shift-job-select">
                  <option value="">No job</option>
                  {(jobs || []).map((j) => <option key={j.id} value={j.id}>{j.title}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-sm font-semibold">Start date</Label>
                <Input type="date" value={form.start_date} onChange={(e) => set('start_date', e.target.value)} className="mt-1" data-testid="shift-start-date" />
              </div>
              <div>
                <Label className="text-sm font-semibold">End date</Label>
                <Input type="date" value={form.end_date} onChange={(e) => set('end_date', e.target.value)} className="mt-1" data-testid="shift-end-date" />
              </div>
              <div>
                <Label className="text-sm font-semibold">Start time</Label>
                <Input type="time" value={form.start_time} onChange={(e) => set('start_time', e.target.value)} className="mt-1" data-testid="shift-start-time" />
              </div>
              <div>
                <Label className="text-sm font-semibold">End time</Label>
                <Input type="time" value={form.end_time} onChange={(e) => set('end_time', e.target.value)} className="mt-1" data-testid="shift-end-time" />
              </div>
            </div>

            <div>
              <Label className="text-sm font-semibold">Repeats</Label>
              <div className="flex items-center gap-4 mt-1">
                <select value={form.recurrence} onChange={(e) => set('recurrence', e.target.value)} className={`${inputCls} !mt-0 w-44`} data-testid="shift-recurrence-select">
                  <option value="once">One-time</option>
                  <option value="weekly">Weekly</option>
                </select>
                {form.recurrence === 'weekly' && (
                  <div className="flex gap-1 flex-wrap">
                    {WEEKDAYS.map((d) => (
                      <button key={d.i} type="button" onClick={() => toggleIn('days_of_week', d.i)}
                        data-testid={`shift-dow-${d.i}`}
                        className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${
                          form.days_of_week.includes(d.i)
                            ? 'bg-[#1E88E5] text-white border-[#1E88E5]'
                            : 'bg-white dark:bg-[#1A2433] text-gray-600 dark:text-gray-300 border-gray-300 dark:border-[#2D3748]'
                        }`}>
                        {d.l}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {form.recurrence === 'weekly' && (
                <p className="text-xs text-slate-500 mt-1">Repeats on the checked weekdays between start and end date.</p>
              )}
            </div>

            <div>
              <Label className="text-sm font-semibold">
                Who&apos;s on this shift? {form.member_ids.length === 0 ? '(whole team)' : `(${form.member_ids.length} selected)`}
              </Label>
              <div className="max-h-36 overflow-y-auto space-y-1 border border-gray-200 dark:border-[#2D3748] rounded-lg p-2 mt-1">
                {(members || []).map((m) => (
                  <label key={m.id} className="flex items-center gap-2 text-sm cursor-pointer py-0.5" data-testid={`shift-member-${m.id}`}>
                    <Checkbox checked={form.member_ids.includes(m.id)} onCheckedChange={() => toggleIn('member_ids', m.id)} />
                    <span className="text-gray-800 dark:text-gray-200">{m.name}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-1">Leave everyone unchecked to schedule the whole team.</p>
            </div>

            <div>
              <Label className="text-sm font-semibold">Notes (optional)</Label>
              <Input value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder="Bring PPE, meet at north gate…" className="mt-1" data-testid="shift-notes-input" />
            </div>

            <div className="flex gap-3">
              <Button onClick={save} disabled={saving} className="flex-1 bg-[#1E88E5] hover:bg-blue-700 text-white" data-testid="save-shift-btn">
                {editingId ? 'Save Changes' : 'Schedule Shift'}
              </Button>
              <Button variant="outline" className="flex-1" onClick={() => setShowForm(false)}>Cancel</Button>
            </div>
          </div>
        )}

        {/* Week grid — drag one-time shifts between days (Jun 2026, Phase 3) */}
        <div data-testid="schedule-week-grid">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <div className="flex items-center gap-1">
              <Button size="sm" variant="ghost" onClick={() => setWeekStart(addDays(weekStart, -7))} data-testid="week-prev-btn">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 min-w-[150px] text-center" data-testid="week-label">{weekLabel}</span>
              <Button size="sm" variant="ghost" onClick={() => setWeekStart(addDays(weekStart, 7))} data-testid="week-next-btn">
                <ChevronRight className="w-4 h-4" />
              </Button>
              {weekStart !== mondayOf(today) && (
                <Button size="sm" variant="outline" className="h-7 text-xs ml-1" onClick={() => setWeekStart(mondayOf(today))} data-testid="week-today-btn">This week</Button>
              )}
            </div>
            <Button size="sm" variant="outline" onClick={copyLastWeek} disabled={copying}
              className="border-[#1E88E5]/50 text-[#1E88E5]" data-testid="copy-last-week-btn">
              <Copy className="w-3.5 h-3.5 mr-1.5" /> {copying ? 'Copying…' : 'Copy Last Week'}
            </Button>
          </div>

          <div className="grid grid-cols-7 gap-1.5">
            {weekDates.map((date, i) => {
              const occs = occByDate[date] || [];
              const ptos = ptoByDate[date] || [];
              return (
                <div key={date}
                  onDragOver={(e) => { e.preventDefault(); setDragOverDay(date); }}
                  onDragLeave={() => setDragOverDay((d) => (d === date ? null : d))}
                  onDrop={(e) => onDrop(date, e)}
                  className={`rounded-lg border min-h-[120px] p-1.5 transition-colors ${
                    dragOverDay === date
                      ? 'border-[#1E88E5] bg-blue-50 dark:bg-blue-950/30'
                      : date === today
                        ? 'border-[#1E88E5]/50 bg-blue-50/40 dark:bg-[#0F1923]'
                        : 'border-gray-200 dark:border-[#2D3748] bg-white dark:bg-[#0F1923]'
                  }`}
                  data-testid={`grid-day-${date}`}
                >
                  <p className={`text-[11px] font-bold mb-1 ${date === today ? 'text-[#1E88E5]' : 'text-slate-500'}`}>
                    {WEEKDAYS[i].l} <span className="font-normal">{date.slice(8)}</span>
                  </p>
                  <div className="space-y-1">
                    {ptos.map((p, j) => (
                      <div key={`pto-${j}`}
                        className="rounded px-1.5 py-1 text-[10px] leading-tight font-medium bg-amber-100 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 border border-amber-300 dark:border-amber-700/50"
                        title={`${p.user_name} — approved ${p.type} time off`}
                        data-testid={`grid-pto-${date}-${j}`}
                      >
                        <Plane className="w-2.5 h-2.5 inline mr-0.5 -mt-0.5" />{p.user_name} · {p.type}
                      </div>
                    ))}
                    {occs.map((o, j) => (
                      <div key={`${o.shift_id}-${j}`}
                        draggable={o.recurrence !== 'weekly'}
                        onDragStart={(e) => e.dataTransfer.setData('text/plain', JSON.stringify({ shift_id: o.shift_id, date }))}
                        onClick={() => { const s = shifts.find((x) => x.id === o.shift_id); if (s) openEdit(s); }}
                        className={`rounded px-1.5 py-1 text-[10px] leading-tight cursor-pointer border ${
                          o.recurrence === 'weekly'
                            ? 'bg-violet-100 dark:bg-violet-950/40 text-violet-800 dark:text-violet-300 border-violet-300 dark:border-violet-700/50'
                            : 'bg-blue-100 dark:bg-blue-950/40 text-blue-800 dark:text-blue-300 border-blue-300 dark:border-blue-700/50 cursor-grab active:cursor-grabbing'
                        }`}
                        title={`${o.title || 'Shift'} ${o.start_time}–${o.end_time} · ${o.member_names.join(', ')}${o.recurrence === 'weekly' ? ' (weekly — edit to change days)' : ' (drag to move)'}`}
                        data-testid={`grid-shift-${date}-${o.shift_id}`}
                      >
                        <p className="font-semibold truncate">
                          {o.recurrence === 'weekly' && <Repeat className="w-2.5 h-2.5 inline mr-0.5 -mt-0.5" />}
                          {o.title || 'Shift'}
                        </p>
                        <p className="opacity-80">{o.start_time}–{o.end_time}</p>
                        <p className="opacity-70 truncate">{o.member_names.slice(0, 2).join(', ')}{o.member_names.length > 2 ? ` +${o.member_names.length - 2}` : ''}</p>
                      </div>
                    ))}
                    <button type="button" onClick={() => openCreate(date)}
                      className="w-full rounded border border-dashed border-gray-300 dark:border-[#2D3748] text-slate-400 hover:text-[#1E88E5] hover:border-[#1E88E5]/60 text-[10px] py-0.5 transition-colors"
                      data-testid={`grid-add-${date}`}
                    >+ add</button>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-[11px] text-slate-500 mt-1.5">
            Drag a blue shift to another day to move it. Purple shifts repeat weekly — edit them to change days. Amber = approved time off.
          </p>
        </div>

        {/* Shift definitions (edit/delete) */}
        {shifts.length > 0 && (
          <div>
            <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2">All shifts on this team</p>
            <div className="space-y-1" data-testid="shift-definitions">
              {shifts.map((s) => (
                <div key={s.id} className="flex items-center justify-between p-2 rounded-lg border border-gray-200 dark:border-[#2D3748]">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {s.title || 'Shift'} · {s.start_time}–{s.end_time}
                      {s.recurrence === 'weekly' && (
                        <span className="text-xs text-slate-500 ml-1">
                          (weekly: {(s.days_of_week || []).map((d) => WEEKDAYS[d]?.l).join(', ')})
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-slate-500">
                      {s.start_date}{s.end_date && s.end_date !== s.start_date ? ` → ${s.end_date}` : ''} ·{' '}
                      {(s.member_ids || []).length ? `${s.member_ids.length} member(s)` : 'Whole team'}
                    </p>
                  </div>
                  <div className="flex gap-1 shrink-0 ml-2">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(s)} data-testid={`edit-shift-${s.id}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-600" onClick={() => remove(s)} data-testid={`delete-shift-${s.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
